import {Link, Stack, Typography} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import BookIcon from "@mui/icons-material/AutoStoriesOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import YoutubeIcon from "../../assets/icons/youtube.svg";
import {Dialog} from "../base/Dialog";
import {useTranslation} from "react-i18next";
import {Frame} from "../base/Frame";
import {PropsWithChildren} from "react";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const DocumentationDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    return (
        <Dialog
            title={t("dialogs.documentation.title")}
            titleIcon={<HelpIcon color="primary" />}
            cancelTitle={t("dialogs.actions.close")}
            open={open}
            onClose={() => onClose(false)}
        >
            <Stack direction="column" gap="1.5rem">
                <DocumentationSection icon={<BookIcon sx={{color: "#7587D1", width: "2rem", height: "2rem"}} />}>
                    <Typography fontSize={18} fontWeight={500} color="#2B303F" textTransform="uppercase">
                        {t("dialogs.documentation.user_manual")}
                    </Typography>
                    <Typography
                        fontSize={14}
                        fontWeight={400}
                        color="#3A3F4F"
                        dangerouslySetInnerHTML={{__html: t("dialogs.documentation.user_manual_description")}}
                    ></Typography>
                    <Stack direction="column" gap="0.75rem">
                        <LocalizedResource
                            href="/documentation/irriwatch-user-guide.pdf"
                            label="In English"
                            language="EN"
                        />
                        <LocalizedResource
                            href="/documentation/irriwatch-user-guide-IT.pdf"
                            label="In Italiano"
                            language="IT"
                        />
                        <LocalizedResource
                            href="/documentation/irriwatch-user-guide-ES.pdf"
                            label="En Español"
                            language="ES"
                        />
                        <LocalizedResource
                            href="/documentation/user-input.pdf"
                            label="Appendix B - User Inputs"
                            language="EN"
                        />
                    </Stack>
                </DocumentationSection>
                <DocumentationSection icon={<BookIcon sx={{color: "#7587D1", width: "2rem", height: "2rem"}} />}>
                    <Typography fontSize={18} fontWeight={500} color="#2B303F" textTransform="uppercase">
                        {t("dialogs.documentation.quick_start")}
                    </Typography>
                    <Typography
                        fontSize={14}
                        fontWeight={400}
                        color="#3A3F4F"
                        dangerouslySetInnerHTML={{__html: t("dialogs.documentation.quick_start_description")}}
                    ></Typography>
                    <Stack direction="column" gap="0.75rem">
                        <LocalizedResource
                            href="/documentation/quickstart-guide.pdf"
                            label="In English"
                            language="EN"
                        />
                        <LocalizedResource
                            href="/documentation/quickstart-guide-es.pdf"
                            label="En Español"
                            language="ES"
                        />
                        <LocalizedResource
                            href="/documentation/quickstart-guide-it.pdf"
                            label="In Italiano"
                            language="IT"
                        />
                        <LocalizedResource
                            href="/documentation/quickstart-guide-fr.pdf"
                            label="En Français"
                            language="FR"
                        />
                        <OtherResource
                            href="https://www.youtube.com/watch?v=fiue9qxkBdE"
                            label={t("dialogs.documentation.on_youtube")}
                        />
                    </Stack>
                </DocumentationSection>
                <DocumentationSection icon={<SettingsIcon sx={{color: "#7587D1", width: "2rem", height: "2rem"}} />}>
                    <Typography fontSize={18} fontWeight={500} color="#2B303F" textTransform="uppercase">
                        {t("dialogs.documentation.in_depth_documentation")}
                    </Typography>
                    <Typography fontSize={14} fontWeight={400} color="#3A3F4F">
                        {t("dialogs.documentation.in_depth_documentation_description")}
                    </Typography>
                    <Stack direction="column" gap="0.75rem">
                        <LocalizedResource
                            href="/documentation/api-documentation-en.pdf"
                            label="API Documentation"
                            language="EN"
                        />
                        <LocalizedResource
                            href="/documentation/api-practical-steps.pdf"
                            label="Guide for API Swagger"
                            language="EN"
                        />
                    </Stack>
                </DocumentationSection>
                <DocumentationSection icon={<img src={YoutubeIcon} />}>
                    <Typography fontSize={18} fontWeight={500} color="#2B303F" textTransform="uppercase">
                        {t("dialogs.documentation.other_resources")}
                    </Typography>
                    <Typography fontSize={14} fontWeight={400} color="#3A3F4F">
                        {t("dialogs.documentation.other_resources_description")}
                    </Typography>
                    <Stack direction="column" gap="0.75rem">
                        <OtherResource
                            href="https://www.youtube.com/@irriwatch8602"
                            label={t("dialogs.documentation.irriwatch_on_youtube")}
                        />
                        <OtherResource
                            href="https://www.linkedin.com/company/hydrosat"
                            label={t("dialogs.documentation.hydrosat_on_linkedin")}
                        />
                    </Stack>
                </DocumentationSection>
            </Stack>
        </Dialog>
    );
};

interface DocumentationSectionProps extends PropsWithChildren {
    icon: React.ReactNode;
}

const DocumentationSection: React.FC<DocumentationSectionProps> = ({icon, children}) => (
    <Frame sx={{paddingY: "1.5rem", paddingX: "1.875rem"}}>
        <Stack direction="row" gap="1.875rem">
            <Stack
                direction="column"
                sx={{
                    backgroundColor: "#F3F5FA",
                    width: "3.75rem",
                    height: "3.75rem",
                    minWidth: "3.75rem",
                    borderRadius: "0.25rem",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {icon}
            </Stack>
            <Stack direction="column" gap="1.5rem">
                {children}
            </Stack>
        </Stack>
    </Frame>
);

const LocalizedResource: React.FC<{href: string; label: string; language: string}> = ({href, label, language}) => (
    <Stack direction="row" gap="1rem" alignItems="center">
        <Stack
            sx={{width: "2.375rem", height: "1.625rem", backgroundColor: "#F3F5FA"}}
            alignItems="center"
            justifyContent="center"
        >
            <Typography color="#404DB8" fontSize={16}>
                {language}
            </Typography>
        </Stack>
        <Link href={href} target="_blank" rel="noreferrer">
            {label}
        </Link>
    </Stack>
);

const OtherResource: React.FC<{href: string; label: string}> = ({href, label}) => (
    <Stack direction="row" gap="1rem" alignItems="center">
        <ArrowForwardIcon sx={{color: "#404DB8", width: "1.125rem", height: "1.125rem", marginX: "0.5rem"}} />
        <Link href={href} target="_blank" rel="noreferrer">
            {label}
        </Link>
    </Stack>
);
